import { lazy, Suspense } from "react";
import type { HeadersFunction, MetaFunction } from "react-router";
import { LoaderFunction, useLoaderData } from "react-router";
import AboutUs from "~/components/home-screen/AboutUs";
import Hero from "~/components/home-screen/Hero";
import type { RecentUpdate } from "~/components/home-screen/RecentUpdates";
import { ArticleType } from "~/interface/article/article";
import { DarshanImage } from "~/interface/daily-darshan/daily-darshan";
import { HeroBanner, LiveData, NewsItem } from "~/interface/home/home";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { cn } from "~/lib/utils";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

// Lazy load non-critical components for better code splitting
// const AboutUs = lazy(() => import("~/components/home-screen/AboutUs"));
const Announcements = lazy(() => import("~/components/home-screen/Announcements"));
const DailyDarshanHome = lazy(() => import("~/components/home-screen/DailyDarshanHome"));
const LiveTvRadio = lazy(() => import("~/components/home-screen/LiveTvRadio"));
const RecentUpdates = lazy(() =>
  import("~/components/home-screen/RecentUpdates").then((m) => ({
    default: m.default,
  })),
);

interface loaderdata {
  heroBannersData: HeroBanner[] | null;
  liveDataArray: LiveData[];
  newsData: NewsItem[];
  announcementData: ArticleType[];
  dailydarshanData: Record<string, DarshanImage[]>;
  availableDataDatesArray: string[];
  recentUpdate: RecentUpdate[];
  baseUrl: string;
}

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const { lang } = params;
  const meta = getPageMeta("home", lang || "en");

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage ?? FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth ?? "1200",
    ogHeight: meta.ogImageHeight ?? "630",
    canonicalLink: `${pathname}`,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader: LoaderFunction = async ({ params, request }) => {
  const lang = params.lang;

  const apiService = new ApiService(lang || LANG.english);

  // const dailyDarshanPayload = {
  //   start_date: dayjs(new Date()).format("YYYY-MM-DD"),
  //   n_days: 1,
  // };

  const mandir = "karelibaug";

  const response = await apiService.getHomeScreenData();
  const dailydarshanData = response?.daily_darshan || [];
  const heroBannersData = response?.hero_banners;
  const liveDataArray = response?.live_data;
  const newsData = response?.news;
  const recentUpdate = response?.latest_updates || [];
  const announcementData: ArticleType[] = response?.announcements || [];

  const etag = computeEtagFromData({
    heroBannersData,
    liveDataArray,
    newsData,
    announcementData,
    dailydarshanData,
    recentUpdate,
  });

  const ifNoneMatch = request.headers.get("If-None-Match");

  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(
    {
      heroBannersData: heroBannersData || [],
      liveDataArray,
      newsData,
      announcementData,
      dailydarshanData,
      recentUpdate,
    },
    {
      headers: {
        ...CACHED_PAGE.HOME.HEADERS,
        ETag: etag,
      },
    },
  );
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

const ROUNDED = "2rem";

export default function Index() {
  const data = useLoaderData<loaderdata>();

  // const isDesktop = useMediaQuery("(min-width: 768px)");

  // Re-enable scroll animations now that the scroll issue is fixed in MandirTabs
  // const { scrollY } = useScroll();

  /* WIDTH ─────────────────────────────────────── */
  // const clampedScrollY = useTransform(scrollY, (value) => Math.max(0, value));

  // const rawWidth = useTransform(
  //   clampedScrollY,
  //   [200, 400, 1800, 2200], // input range (px) - adjusted for removed HeritageNew
  //   ["90%", "100%", "100%", "90%"], // output range (width)
  // );

  // const width = useSpring(rawWidth, { stiffness: 80, damping: 15, restDelta: 0.001 });

  /* RADIUS ────────────────────────────────────── */
  // const rawRadius = useTransform(
  //   clampedScrollY,
  //   [150, 350, 1800, 2200], // adjusted for removed HeritageNew
  //   [ROUNDED, ROUNDED, "0rem", ROUNDED], // 0 when full-width, curved otherwise
  // );

  // const borderRadius = useSpring(rawRadius, { stiffness: 80, damping: 15, restDelta: 0.001 });
  return (
    <div
      className="w-full flex flex-col xs:gap-10 sm:gap-4 md:gap-6 justify-start items-center"
      style={{ scrollBehavior: "auto", overflowAnchor: "none" }}
    >
      {/* Hero Section */}
      {data?.heroBannersData && data?.heroBannersData?.length > 0 && (
        <div className="body-container flex justify-start items-center">
          <Hero images={data?.heroBannersData} />
        </div>
      )}

      {/* Heritage new Section - Testing if this causes scroll issue */}
      {/* <div className="w-full flex justify-center items-start">
        <div className="body-container p-4 lg:p-2 flex justify-center items-start">
          <HeritageNew />
        </div>
      </div> */}

      {/* About Us Section */}
      <div className="w-full flex justify-center items-center mt-5 lg:mt-10">
        <div className="flex items-start justify-start md:justify-center md:items-center bg-darkBackground p-5 md:py-5 md:px-10  overflow-hidden rounded-2xl">
          <div className="body-container flex flex-col justify-start items-start gap-8">
            <Suspense fallback={<div className="w-full h-80 bg-darkBackground animate-pulse rounded-lg" />}>
              <AboutUs />
            </Suspense>
          </div>
        </div>
      </div>

      {/* Daily Darshan Section */}
      <div className="body-container flex justify-center items-center ">
        <Suspense fallback={<div className="w-full h-96 bg-darkBackground animate-pulse rounded-lg" />}>
          <DailyDarshanHome dailyDarshan={data?.dailydarshanData} />
        </Suspense>
      </div>

      {/* Live TV & Radio Section */}
      <div className="w-full py-5 px-2 md:px-10 2xl:p-8 flex justify-center items-center bg-darkBackground">
        {data?.liveDataArray && data?.liveDataArray.length > 0 && (
          <div className="relative body-container px-4 flex justify-center items-center overflow-hidden">
            <Suspense fallback={<div className="w-full h-64 bg-darkBackground animate-pulse rounded-lg" />}>
              <LiveTvRadio data={data?.liveDataArray} />
            </Suspense>
            <div
              className="absolute inset-0 w-full h-full opacity-100 -z-10 max-md:hidden flex-shrink-0"
              style={{
                backgroundImage: `url(/images/homepage-design.png)`,
                backgroundRepeat: "no-repeat",
              }}
            ></div>
          </div>
        )}
      </div>

      {/* Recent Updates Section */}
      {data.recentUpdate.length > 0 ? (
        <div
          className={cn("body-container py-4 lg:py-0 flex justify-center items-center overflow-hidden", data.announcementData.length < 1 && "mb-5")}
        >
          <div className="flex">
            <Suspense fallback={<div className="w-full h-64 bg-darkBackground animate-pulse rounded-lg" />}>
              <RecentUpdates data={data.recentUpdate} />
            </Suspense>
          </div>
        </div>
      ) : null}

      {/* Announcements Section */}
      {data.announcementData.length > 0 && (
        <div className="w-full flex justify-center items-center bg-darkBackground overflow-hidden">
          <div className="body-container flex justify-center items-center">
            <Suspense fallback={<div className="w-full h-80 bg-darkBackground animate-pulse rounded-lg" />}>
              <Announcements data={data.announcementData} />
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
}
